import React from 'react'

import { Box, Divider, Stack, Tooltip, TooltipProps } from '@mui/material'

import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

type BreakdownItem = {
  label: string
  value: number | string
}

type CategoryBreakdown = {
  title: string
  total: number | string
  items: BreakdownItem[]
}

export type TooltipToReceiveProps = {
  children: React.ReactElement
  leads: {
    total: number | string
    not_due: number | string
    due_up_to_7_days: number | string
    due_more_than_7_days: number | string
  }
  customers: {
    total: number | string
    not_due: number | string
    due_up_to_7_days: number | string
    due_more_than_7_days: number | string
  }
  churn: number | string
  placement?: TooltipProps['placement']
}

const CategorySection = ({ title, total, items }: CategoryBreakdown) => (
  <Box>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
    >
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: theme.palette.neutral[100],
          fontSize: '14px'
        }}
      >
        {title}
      </Box>
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          fontSize: '14px'
        }}
      >
        {typeof total === 'number' ? formatMoney(total) : total}
      </Box>
    </Stack>
    <Stack gap={0.5} pl={1}>
      {items.map((item, index) => (
        <Stack
          key={index}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            component="span"
            sx={{
              color: theme.palette.neutral[400],
              fontSize: '12px'
            }}
          >
            {item.label}
          </Box>
          <Box
            component="span"
            sx={{
              color: theme.palette.neutral[200],
              fontSize: '12px'
            }}
          >
            {typeof item.value === 'number'
              ? formatMoney(item.value)
              : item.value}
          </Box>
        </Stack>
      ))}
    </Stack>
  </Box>
)

const TooltipToReceive = ({
  children,
  leads,
  customers,
  churn,
  placement = 'bottom'
}: TooltipToReceiveProps) => {
  const tooltipContent = (
    <Box
      sx={{
        p: 2,
        minWidth: 280,
        maxWidth: 320
      }}
    >
      <Box
        sx={{
          fontSize: '13px',
          fontWeight: 500,
          color: theme.palette.neutral[300],
          mb: 2,
          textAlign: 'center'
        }}
      >
        Detalhamento do Previsto
      </Box>

      <Stack gap={2}>
        <CategorySection
          title="Leads"
          total={leads.total}
          items={[
            { label: 'Não vencidos', value: leads.not_due },
            { label: 'Vencidos até 7 dias', value: leads.due_up_to_7_days },
            {
              label: 'Vencidos há mais de 7 dias',
              value: leads.due_more_than_7_days
            }
          ]}
        />

        <Divider sx={{ borderColor: theme.palette.neutral[700] }} />

        <CategorySection
          title="Clientes"
          total={customers.total}
          items={[
            { label: 'Não vencidos', value: customers.not_due },
            { label: 'Vencidos até 7 dias', value: customers.due_up_to_7_days },
            {
              label: 'Vencidos há mais de 7 dias',
              value: customers.due_more_than_7_days
            }
          ]}
        />

        <Divider sx={{ borderColor: theme.palette.neutral[700] }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              color: theme.palette.error[300],
              fontSize: '14px'
            }}
          >
            Canceladas/Expiradas
          </Box>
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              color: theme.palette.error[300],
              fontSize: '14px'
            }}
          >
            {typeof churn === 'number' ? formatMoney(churn) : churn}
          </Box>
        </Stack>
      </Stack>
    </Box>
  )

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: theme.palette.neutral[800],
            border: `1px solid ${theme.palette.neutral[600]}`,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            '& .MuiTooltip-arrow': {
              color: theme.palette.neutral[800],
              '&::before': {
                border: `1px solid ${theme.palette.neutral[600]}`
              }
            }
          }
        }
      }}
    >
      {children}
    </Tooltip>
  )
}

export default TooltipToReceive
